'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { HeadphonesIcon, ArrowLeft, MessageCircle, Mail, Clock } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

type Lang = 'en' | 'ar' | 'fr';

const WHATSAPP_NUMBER = '213663227895';

const translations = {
  en: {
    dir: 'ltr' as const,
    back: 'Back to Home',
    badge: 'Support',
    title: 'We\'re Here to Help',
    subtitle: 'Have a question or need assistance? Reach out to us directly — we typically respond within a few hours.',
    whatsapp: {
      title: 'WhatsApp Support',
      desc: 'Chat with us directly on WhatsApp for the fastest response.',
      cta: 'Open WhatsApp',
      number: '+213 663 227 895',
    },
    email: {
      title: 'Email Support',
      desc: 'Send us a detailed message and we\'ll get back to you within 24 hours.',
      cta: 'Send Email',
      address: 'support@wealthos.app',
    },
    hours: {
      title: 'Support Hours',
      desc: 'Our team is available Sunday through Thursday, 9 AM – 6 PM (GMT+1).',
    },
    faq: {
      title: 'Common Questions',
      items: [
        {
          q: 'How do I upgrade my plan?',
          a: 'Go to Settings → Subscription and choose the plan that fits your needs. All paid plans include a free trial.',
        },
        {
          q: 'How do I reset my password?',
          a: 'Click "Forgot Password" on the login page and follow the instructions sent to your email.',
        },
        {
          q: 'Can I export my financial data?',
          a: 'Pro users can export PDF reports. Pro Max users get PDF, Excel, and JSON exports from the Financial Reports section.',
        },
        {
          q: 'How do I cancel my subscription?',
          a: 'Go to Settings → Subscription → Manage Plan. You can cancel anytime and your access continues until the end of the billing period.',
        },
      ],
    },
  },
  ar: {
    dir: 'rtl' as const,
    back: 'العودة للرئيسية',
    badge: 'الدعم',
    title: 'نحن هنا لمساعدتك',
    subtitle: 'هل لديك سؤال أو تحتاج إلى مساعدة؟ تواصل معنا مباشرةً — نرد عادةً في غضون ساعات قليلة.',
    whatsapp: {
      title: 'دعم واتساب',
      desc: 'تحدث معنا مباشرةً على واتساب للحصول على أسرع رد.',
      cta: 'فتح واتساب',
      number: '+213 663 227 895',
    },
    email: {
      title: 'دعم البريد الإلكتروني',
      desc: 'أرسل لنا رسالة مفصّلة وسنرد عليك خلال 24 ساعة.',
      cta: 'إرسال بريد إلكتروني',
      address: 'support@wealthos.app',
    },
    hours: {
      title: 'ساعات الدعم',
      desc: 'فريقنا متاح من الأحد إلى الخميس، من 9 صباحاً حتى 6 مساءً (GMT+1).',
    },
    faq: {
      title: 'الأسئلة الشائعة',
      items: [
        {
          q: 'كيف أرقّي خطتي؟',
          a: 'اذهب إلى الإعدادات ← الاشتراك واختر الخطة التي تناسب احتياجاتك. جميع الخطط المدفوعة تتضمن فترة تجريبية مجانية.',
        },
        {
          q: 'كيف أعيد تعيين كلمة المرور؟',
          a: 'انقر على "نسيت كلمة المرور" في صفحة تسجيل الدخول واتبع التعليمات المرسلة إلى بريدك الإلكتروني.',
        },
        {
          q: 'هل يمكنني تصدير بياناتي المالية؟',
          a: 'يمكن لمستخدمي Pro تصدير تقارير PDF. يحصل مستخدمو Pro Max على تصدير PDF وExcel وJSON من قسم التقارير المالية.',
        },
        {
          q: 'كيف أُلغي اشتراكي؟',
          a: 'اذهب إلى الإعدادات ← الاشتراك ← إدارة الخطة. يمكنك الإلغاء في أي وقت ويستمر وصولك حتى نهاية فترة الفوترة.',
        },
      ],
    },
  },
  fr: {
    dir: 'ltr' as const,
    back: 'Retour à l\'accueil',
    badge: 'Support',
    title: 'Nous sommes là pour vous aider',
    subtitle: 'Vous avez une question ou besoin d\'aide ? Contactez-nous directement — nous répondons généralement en quelques heures.',
    whatsapp: {
      title: 'Support WhatsApp',
      desc: 'Discutez avec nous directement sur WhatsApp pour une réponse rapide.',
      cta: 'Ouvrir WhatsApp',
      number: '+213 663 227 895',
    },
    email: {
      title: 'Support par e-mail',
      desc: 'Envoyez-nous un message détaillé et nous vous répondrons dans les 24 heures.',
      cta: 'Envoyer un e-mail',
      address: 'support@wealthos.app',
    },
    hours: {
      title: 'Heures de support',
      desc: 'Notre équipe est disponible du dimanche au jeudi, de 9h à 18h (GMT+1).',
    },
    faq: {
      title: 'Questions fréquentes',
      items: [
        {
          q: 'Comment mettre à niveau mon plan ?',
          a: 'Allez dans Paramètres → Abonnement et choisissez le plan qui correspond à vos besoins. Tous les plans payants incluent un essai gratuit.',
        },
        {
          q: 'Comment réinitialiser mon mot de passe ?',
          a: 'Cliquez sur "Mot de passe oublié" sur la page de connexion et suivez les instructions envoyées à votre e-mail.',
        },
        {
          q: 'Puis-je exporter mes données financières ?',
          a: 'Les utilisateurs Pro peuvent exporter des rapports PDF. Les utilisateurs Pro Max obtiennent des exports PDF, Excel et JSON depuis la section Rapports financiers.',
        },
        {
          q: 'Comment annuler mon abonnement ?',
          a: 'Allez dans Paramètres → Abonnement → Gérer le plan. Vous pouvez annuler à tout moment et votre accès continue jusqu\'à la fin de la période de facturation.',
        },
      ],
    },
  },
};

export default function SupportPage() {
  const [lang, setLang] = useState<Lang>('ar');
  const t = translations[lang];
  const isRtl = t.dir === 'rtl';

  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}`;

  return (
    <div className="min-h-screen bg-background" dir={t.dir}>
      {/* Nav */}
      <nav className="sticky top-0 z-40 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link href="/welcome" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft size={16} className={isRtl ? 'rotate-180' : ''} />
            {t.back}
          </Link>
          <div className="flex items-center gap-2">
            <AppLogo size={24} />
            <span className="text-sm font-semibold text-foreground">Wealth<span className="text-primary">OS</span></span>
          </div>
          <div className="flex items-center gap-1 bg-muted/50 rounded-xl p-1">
            {(['en', 'ar', 'fr'] as Lang[]).map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                  lang === l ? 'bg-primary text-primary-foreground shadow' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/10 mb-5">
            <HeadphonesIcon size={13} className="text-primary" />
            <span className="text-xs font-semibold text-primary">{t.badge}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-3">{t.title}</h1>
          <p className="text-sm text-muted-foreground max-w-lg mx-auto leading-relaxed">{t.subtitle}</p>
        </div>

        {/* Contact Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          {/* WhatsApp */}
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="glass-card rounded-2xl p-6 border border-[#25D366]/30 hover:border-[#25D366]/60 transition-all hover-glow group flex flex-col items-center text-center gap-3"
          >
            <div className="w-14 h-14 rounded-2xl bg-[#25D366]/15 flex items-center justify-center group-hover:bg-[#25D366]/25 transition-colors">
              <MessageCircle size={26} className="text-[#25D366]" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-foreground mb-1">{t.whatsapp.title}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed mb-3">{t.whatsapp.desc}</p>
              <p className="text-xs font-mono text-[#25D366] mb-3">{t.whatsapp.number}</p>
              <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#25D366] text-white text-xs font-bold shadow-lg shadow-[#25D366]/25 group-hover:opacity-90 transition-opacity">
                <MessageCircle size={13} />
                {t.whatsapp.cta}
              </span>
            </div>
          </a>

          {/* Email */}
          <a
            href={`mailto:${t.email.address}`}
            className="glass-card rounded-2xl p-6 border border-border hover:border-primary/40 transition-all hover-glow group flex flex-col items-center text-center gap-3"
          >
            <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
              <Mail size={26} className="text-primary" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-foreground mb-1">{t.email.title}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed mb-3">{t.email.desc}</p>
              <p className="text-xs font-mono text-primary mb-3">{t.email.address}</p>
              <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-primary/40 text-primary text-xs font-bold group-hover:bg-primary/10 transition-colors">
                <Mail size={13} />
                {t.email.cta}
              </span>
            </div>
          </a>

          {/* Hours */}
          <div className="glass-card rounded-2xl p-6 border border-border flex flex-col items-center text-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-muted/50 flex items-center justify-center">
              <Clock size={26} className="text-muted-foreground" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-foreground mb-1">{t.hours.title}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">{t.hours.desc}</p>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="glass-card rounded-2xl border border-border overflow-hidden">
          <div className="px-6 py-4 border-b border-border/50">
            <h2 className="text-base font-bold text-foreground">{t.faq.title}</h2>
          </div>
          <div className="divide-y divide-border/50">
            {t.faq.items.map((item) => (
              <div key={item.q} className="px-6 py-5">
                <p className="text-sm font-semibold text-foreground mb-2">{item.q}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer links */}
        <div className="mt-12 pt-8 border-t border-border/50 flex flex-wrap items-center justify-center gap-6 text-xs text-muted-foreground">
          <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
          <Link href="/terms" className="hover:text-foreground transition-colors">Terms</Link>
          <Link href="/support" className="text-primary font-semibold">Support</Link>
        </div>
      </main>
    </div>
  );
}
